import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { FolderPathSegment } from "../../utils/folderPathResolver";

interface BreadcrumbTooltipContentProps {
  segments: FolderPathSegment[];
  buildingId: string;
}

/**
 * Interactive breadcrumb content for document path tooltips.
 * Project/folder segments are clickable and navigate to the Files page's Folders tab.
 */
export function BreadcrumbTooltipContent({ segments, buildingId }: BreadcrumbTooltipContentProps) {
  const navigate = useNavigate();

  const handleSegmentClick = (segment: FolderPathSegment) => {
    if (segment.type === "filename") return;
    const projectId = segment.projectId;
    if (!projectId) return;

    // Legacy param pair on purpose: the Files page migrates `?projectId=&folderId=`
    // onto the global `?project=` scope and opens the Folders tab at that
    // folder — the plain scope param alone cannot open a specific folder.
    const params = new URLSearchParams({ projectId });
    if (segment.type === "folder" && segment.folderId) {
      params.set("folderId", segment.folderId);
    }
    navigate(`/buildings/${buildingId}/documents?${params.toString()}`);
  };

  return (
    <span className="flex flex-wrap items-center gap-1">
      {segments.map((segment, i) => {
        const isFirst = i === 0;
        const isLast = i === segments.length - 1;
        const isClickable = segment.type !== "filename" && !!segment.projectId;

        return (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="h-3 w-3 flex-shrink-0 text-muted-foreground/60" />}
            {isClickable ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSegmentClick(segment);
                }}
                className={cn(
                  "cursor-pointer hover:text-primary hover:underline",
                  isFirst && "font-semibold",
                  !isFirst && "text-muted-foreground"
                )}
              >
                {segment.label}
              </button>
            ) : (
              <span
                className={cn(
                  isFirst && "font-semibold",
                  isLast && "text-foreground",
                  !isFirst && !isLast && "text-muted-foreground"
                )}
              >
                {segment.label}
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}
